import { toTitleCase } from 'stores/utils';
import { formatDateTimeMS } from './dateTime';
import { getRouteFromPathName } from './localStorage';
import toast from './toast';

export const SIM_LOG_KEY = 'simulcast_log';
export const LS_TOTAL_SIZE_KEY = 'simulcast_ls_size_KB';
export const LS_MAX_SIZE_KB = 5000;
export const SIM_LOG_STORAGE_MAX_KB = () => {
  // console.log(
  //   'max',
  //   localStorage.getItem('debug_maxLogSizeInKb'),
  //   Number(localStorage.getItem('debug_maxLogSizeInKb')),
  //   JSON.parse(localStorage.getItem('debug_maxLogSizeInKb')),
  // );
  //return Number(localStorage.getItem('debug_maxLogSizeInKb'));
  const value = JSON.parse(localStorage.getItem('debug_maxLogSizeInKb'));
  return !value ? Number(value) : value;
};

export const deleteSimLogFromLocalStorage = () => {
  localStorage.removeItem(SIM_LOG_KEY);

  localStorage.removeItem(LS_TOTAL_SIZE_KEY);
};

/* eslint-disable no-console */
export const formattedOutput = ({
  component,
  message,
  messageType,
  method,
  params,
  settings,
  title,
  user,
}) => {
  const output = {};
  const shouldIncludeProp = (field) =>
    settings.propsToShowInOutput.includes(field);
  const now = new Date();
  output.utcTime = now.toUTCString();
  output.localTime = now.toLocaleString('en-US', {
    dateStyle: 'short',
    timeStyle: 'long',
  });
  output.tzOffsetInMinutes = now.getTimezoneOffset();
  output.tzOffsetInHours = now.getTimezoneOffset() / 60;

  if (shouldIncludeProp('component')) output.component = component;
  if (shouldIncludeProp('method')) output.method = method;
  if (shouldIncludeProp('params')) output.params = params;
  if (shouldIncludeProp('message')) output.message = message;
  if (shouldIncludeProp('messageType')) output.messageType = messageType;
  if (shouldIncludeProp('settings')) output.settings = settings;
  if (shouldIncludeProp('title')) output.title = title;
  if (shouldIncludeProp('user')) output.user = user;

  if (settings.toString) return JSON.stringify(output);
  return output;
};

export const LOGGER_SETTINGS = {
  formatter: formattedOutput,
  messageTypeToWrite: ['error'],
  propsToShowInOutput: ['method', 'params', 'message', 'user', 'title'],
  toString: false,
};

export const LOGGER_SETTINGS_DEBUG = Object.assign(LOGGER_SETTINGS, {
  messageTypeToWrite: ['log', 'warn', 'error'],
});

export const getLocalStorageSize = () => {
  let lsTotal = 0,
    xLen,
    x,
    breakdown = [];

  const getSize = (key, size = 0) => {
    return size > 0
      ? {
          key,
          size: getSizeInKb(size).toFixed(2),
          unit: 'KB',
          value: JSON.stringify(localStorage.getItem(key)),
        }
      : { key, size, unit: 'KB' };
  };

  for (x in localStorage) {
    if (!localStorage.hasOwnProperty(x)) {
      continue;
    }
    xLen = (localStorage[x].length + x.length) * 2;
    lsTotal += xLen;
    const size = getSize(x.substr(0, 50), xLen);
    breakdown = [...breakdown, size];
  }

  return [...breakdown, getSize('all', lsTotal)];
};

export const getTotalStorageSize = () => {
  const data = getLocalStorageSize().filter((ls) => ls.key === 'all') || [];
  const sizeOfEverything = data.length && data[0].size;
  const units = data.length && data[0].unit;
  const isSafe = sizeOfEverything < SIM_LOG_STORAGE_MAX_KB();
  const isTooLarge = sizeOfEverything > LS_MAX_SIZE_KB;

  const isApproachingCapacity =
    sizeOfEverything > SIM_LOG_STORAGE_MAX_KB() &&
    sizeOfEverything < LS_MAX_SIZE_KB;

  return {
    isApproachingCapacity,
    isSafe,
    isTooLarge,
    size: sizeOfEverything,
    units,
  };
};

export const simLogTooLarge = () => {
  // console.log('sim', getSimLogSize(), SIM_LOG_STORAGE_MAX_KB());
  return getSimLogSize() > SIM_LOG_STORAGE_MAX_KB();
};

export const getSizeInKb = (value) => Number(value / 1024);
export const getSimLogSize = () => {
  if (!localStorage[SIM_LOG_KEY]) return 0;
  const size = (localStorage[SIM_LOG_KEY]?.length + SIM_LOG_KEY.length) * 2;
  return getSizeInKb(size);
};

export const getSimLogSizeToPrint = () => {
  const size = getSimLogSize();
  if (size === 0) return 'N/A';
  return `${size.toFixed(2)} KB`;
};

class SimulcastLogger {
  constructor({
    component = null,
    method = null,
    params = null,
    message = null,
    messageType = 'error',
    settings = LOGGER_SETTINGS,
    user = null,
    title = null,
    logLevel = null,
    onUpdated = () => null,
  }) {
    this.onUpdated = onUpdated;
    this.component = component;
    this.method = method;
    this.params = params;
    this.message = message;
    this.messageType = messageType;
    this.settings = settings;
    this.user = user;
    this.title = title;
    this.logLevel = logLevel;
  }

  logSetting = () => ({
    isErrorAndLogOnlyErrors:
      this.logLevel === 'errors' && this.messageType === 'error',
    logEverything: this.logLevel === 'verbose',
    loggingIsOff: !this.logLevel,
  });

  shouldWrite = (msgType) =>
    this.messageType === msgType &&
    this.settings.messageTypeToWrite.includes(msgType);

  shouldNotSkipMessageType = () =>
    this.settings.messageTypeToWrite.includes(this.messageType);

  defaultMessageFormatterOutput = () =>
    this.settings.formatter({
      component: this.component,
      message: this.message,
      messageType: this.messageType,
      method: this.method,
      params: this.params,
      settings: this.settings,
      title: this.title,
      user: this.user,
    });

  defaultStorageFormatterOutput = () => {
    const {
      auctionId,
      ipAddress,
      laneId,
      storageParams: { block },
    } = this.params;
    const {
      blockStatus,
      currentBid,
      isOpenForBidding,
      lotNumber,
      monitorChatOnly,
      nextBid,
      productID,
      productState,
      startingBid,
    } = block;
    return {
      auctionId,
      block: {
        blockStatus,
        currentBid,
        isOpenForBidding,
        lotNumber,
        nextBid,
        productID,
        productState,
        startingBid,
      },
      component: this.component,
      ipAddress,
      laneId,
      message: this.message?.toString(),
      messageType: this.messageType,
      method: this.method,
      sessionToken: localStorage.getItem('simulcast_session'),
      slug: toTitleCase(getRouteFromPathName()),
      timestamp: formatDateTimeMS(),
      watchMode: monitorChatOnly,
    };
  };

  logToStorage = (simLogTooLarge) => {
    const {
      isErrorAndLogOnlyErrors,
      logEverything,
      // loggingIsOff,
    } = this.logSetting();

    // if (loggingIsOff) {
    //   localStorage.removeItem(SIM_LOG_KEY);
    //   localStorage.removeItem(LS_TOTAL_SIZE_KEY);
    //   return;
    // }

    const messageOutput = this.defaultStorageFormatterOutput();
    this.onUpdated(messageOutput);

    if (simLogTooLarge) return false;

    if (isErrorAndLogOnlyErrors || logEverything) {
      const ls = localStorage.getItem(SIM_LOG_KEY);
      const log = ls ? JSON.parse(ls) : [];
      // const messageOutput = this.defaultStorageFormatterOutput();
      const updatedLog = [...log, messageOutput];
      localStorage.setItem(SIM_LOG_KEY, JSON.stringify(updatedLog));
      localStorage.setItem(LS_TOTAL_SIZE_KEY, getTotalStorageSize().size);
      // this.onUpdated(messageOutput);
    } else {
      //  this.onUpdated([]);
    }
  };

  logToConsole = () => {
    if (this.shouldNotSkipMessageType()) {
      const messageOutput = this.defaultMessageFormatterOutput();
      const parsedOutput = this.settings.toString
        ? JSON.parse(messageOutput)
        : messageOutput;
      const [, WARN, ERROR] = this.settings.messageTypeToWrite;
      if (this.shouldWrite(WARN)) window.console.warn(parsedOutput);
      if (this.shouldWrite(ERROR)) window.console.error(parsedOutput);
    }
  };

  showToast = (console = false) => {
    if (this.shouldNotSkipMessageType()) {
      const messageOutput = this.defaultMessageFormatterOutput();
      const parsedOutput = this.settings.toString
        ? JSON.parse(messageOutput)
        : messageOutput;
      const { message } = parsedOutput;
      const [LOG, WARN, ERROR] = this.settings.messageTypeToWrite;
      if (this.shouldWrite(LOG)) toast.success(message);
      if (this.shouldWrite(WARN)) toast.success(message);
      if (this.shouldWrite(ERROR)) {
        if (console) this.logToConsole();
        toast.error(message, this.title);
        this.logToStorage();
      }
    }
  };
}

export default SimulcastLogger;
